import { useOutletContext } from "react-router-dom";
import { useData } from "../../context/DataContext";
import { C } from "../../styles/theme";
import Fld from "../../components/common/Fld";
import ImgUploadFld from "../../components/common/ImgUploadFld";
import { ABt } from "../AdminUI";

function Toggle({ on, onToggle }) {
  return (
    <div onClick={onToggle} style={{ width: 40, height: 22, borderRadius: 11, background: on ? "#25D366" : "#cbd5e1", cursor: "pointer", position: "relative", transition: "background .2s", marginLeft: "auto", flexShrink: 0 }}>
      <div style={{ position: "absolute", top: 2, left: on ? 20 : 2, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.2)" }} />
    </div>
  );
}

function Section({ icon, gradient, title, tag, children }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 13, padding: "22px 22px 18px", boxShadow: "0 2px 8px rgba(0,0,0,.04)", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ width: 32, height: 32, background: gradient, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{icon}</div>
        <h3 style={{ color: C.blue, fontWeight: 800, fontSize: 15, margin: 0 }}>{title}</h3>
        {tag && <span style={{ marginLeft: "auto", fontSize: 11, color: C.gray, background: "#f1f5f9", padding: "2px 9px", borderRadius: 20 }}>{tag}</span>}
      </div>
      {children}
    </div>
  );
}

export default function SettingsTab() {
  const { db, setDB } = useData();
  const { toast } = useOutletContext();
  const s = db.settings || {};
  const setSetting = (k, v) => setDB(d => ({ ...d, settings: { ...d.settings, [k]: v } }));

  return (
    <div style={{ maxWidth: 640 }}>

      {/* ── BRANDING & LOGO ── */}
      <Section icon="🎨" gradient="linear-gradient(135deg,#C9972A,#E8C060)" title="Branding & Logo">
        <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "#060f20", borderRadius: 10, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, background: "linear-gradient(135deg,#C9972A,#E8C060)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
            {s.logoUrl
              ? <img src={s.logoUrl} alt="logo" style={{ width: 40, height: 40, objectFit: "cover" }} onError={e => { e.target.style.display = "none"; }} />
              : <span style={{ fontSize: 18 }}>{s.logoEmoji || "⬡"}</span>
            }
          </div>
          <div>
            <div style={{ fontFamily: "'Playfair Display',Georgia,serif", color: "#E8C060", fontWeight: 700, fontSize: 16, lineHeight: 1 }}>{s.brandName || "Cyclojet"}</div>
            <div style={{ color: "rgba(201,151,42,.45)", fontSize: 9, letterSpacing: 1.2, textTransform: "uppercase", marginTop: 3 }}>{s.logoTagline || "Infotech Pvt. Ltd."}</div>
          </div>
          <div style={{ marginLeft: "auto", fontSize: 11, color: "rgba(255,255,255,.3)" }}>Live Preview</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Fld label="Brand Name" value={s.brandName || ""} onChange={v => setSetting("brandName", v)} />
          <Fld label="Logo Emoji (fallback)" value={s.logoEmoji || ""} onChange={v => setSetting("logoEmoji", v)} />
        </div>
        <ImgUploadFld label="Logo Image (leave blank to use emoji)" value={s.logoUrl || ""} onChange={v => setSetting("logoUrl", v)} w={44} h={44} maxDim={400} />
        <Fld label="Tagline under brand name" value={s.logoTagline || ""} onChange={v => setSetting("logoTagline", v)} />
      </Section>

      {/* ── HOMEPAGE STATS ── */}
      <Section icon="📊" gradient="linear-gradient(135deg,#0F6E56,#22c55e)" title="Homepage Stats" tag="shown on homepage counter bar">
        <div style={{ background: "#060f20", borderRadius: 10, padding: "12px 8px", marginBottom: 18, display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 4 }}>
          {[
            [s.placements || "500", s.placementsSuffix || "+", s.placementsLabel || "Successful Placements", "👥"],
            [s.clients || "50", s.clientsSuffix || "+", s.clientsLabel || "Corporate Clients", "🏢"],
            ["Auto", "", "Active Lab SKUs (auto)", "🔬"],
            [s.isoCerts || "5", s.isoCertsSuffix || "", s.isoCertsLabel || "ISO Certifications", "🏅"],
          ].map(([val, sfx, lbl, ic], i) => (
            <div key={i} style={{ textAlign: "center", padding: "8px 4px", borderRight: i < 3 ? "1px solid rgba(255,255,255,.07)" : "none" }}>
              <div style={{ fontSize: 10, color: "rgba(201,151,42,.5)", marginBottom: 3 }}>{ic}</div>
              <div style={{ fontSize: 18, fontWeight: 900, background: "linear-gradient(135deg,#C9972A,#E8C060)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", lineHeight: 1 }}>{val}{sfx}</div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,.35)", marginTop: 4, textTransform: "uppercase", letterSpacing: .5, lineHeight: 1.3 }}>{lbl}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 11, marginBottom: 14 }}>
          <Fld label="Placements Count" value={s.placements || ""} onChange={v => setSetting("placements", v)} />
          <Fld label="Suffix (e.g. +)" value={s.placementsSuffix || ""} onChange={v => setSetting("placementsSuffix", v)} />
          <Fld label="Label" value={s.placementsLabel || ""} onChange={v => setSetting("placementsLabel", v)} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 11, marginBottom: 14 }}>
          <Fld label="Clients Count" value={s.clients || ""} onChange={v => setSetting("clients", v)} />
          <Fld label="Suffix (e.g. +)" value={s.clientsSuffix || ""} onChange={v => setSetting("clientsSuffix", v)} />
          <Fld label="Label" value={s.clientsLabel || ""} onChange={v => setSetting("clientsLabel", v)} />
        </div>
        <div style={{ background: "#eff6ff", border: "1px dashed #93c5fd", borderRadius: 8, padding: "9px 12px", marginBottom: 14, fontSize: 12, color: "#1d4ed8" }}>
          🔬 <strong>Active Lab SKUs</strong> is auto-calculated from your active products — no manual entry needed.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 11 }}>
          <Fld label="ISO Certs Count" value={s.isoCerts || ""} onChange={v => setSetting("isoCerts", v)} />
          <Fld label="Suffix (e.g. blank)" value={s.isoCertsSuffix || ""} onChange={v => setSetting("isoCertsSuffix", v)} />
          <Fld label="Label" value={s.isoCertsLabel || ""} onChange={v => setSetting("isoCertsLabel", v)} />
        </div>
      </Section>

      {/* ── HOMEPAGE CONTENT ── */}
      <Section icon="🏠" gradient="linear-gradient(135deg,#7c3aed,#a78bfa)" title="Homepage" tag="hero · about · announcement">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Fld label="Location Tagline (badge)" value={s.homeHeroTagline || ""} onChange={v => setSetting("homeHeroTagline", v)} />
          <Fld label="Hero CTA Button 1" value={s.homeHeroCta1 || ""} onChange={v => setSetting("homeHeroCta1", v)} />
        </div>
        <Fld label="Hero Headline" value={s.homeHeroHeadline || ""} onChange={v => setSetting("homeHeroHeadline", v)} />
        <Fld label="Hero Subheading" value={s.homeHeroSub || ""} onChange={v => setSetting("homeHeroSub", v)} rows={2} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Fld label="About Section Title" value={s.homeAboutTitle || ""} onChange={v => setSetting("homeAboutTitle", v)} />
          <Fld label="Hero CTA Button 2" value={s.homeHeroCta2 || ""} onChange={v => setSetting("homeHeroCta2", v)} />
        </div>
        <Fld label="About Section Subtext" value={s.homeAboutSub || ""} onChange={v => setSetting("homeAboutSub", v)} rows={2} />
        <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 9, padding: "13px 15px", marginTop: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: C.blue }}>📢 Announcement Banner</div>
            <Toggle on={s.homeAnnouncementEnabled} onToggle={() => setSetting("homeAnnouncementEnabled", !s.homeAnnouncementEnabled)} />
            <span style={{ fontSize: 12, color: s.homeAnnouncementEnabled ? "#15803d" : "#94a3b8", fontWeight: 600 }}>{s.homeAnnouncementEnabled ? "ON" : "OFF"}</span>
          </div>
          <Fld label="Banner Message" value={s.homeAnnouncementBanner || ""} onChange={v => setSetting("homeAnnouncementBanner", v)} />
          <div style={{ fontSize: 11, color: C.gray, marginTop: 4 }}>When enabled, a gold banner appears below the nav on the homepage. Use for promotions or news.</div>
        </div>
      </Section>

      {/* ── STAFFING PAGE ── */}
      <Section icon="👥" gradient="linear-gradient(135deg,#E8593C,#F2A623)" title="Staffing Page" tag="hero · cta">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Fld label="Hero Badge Tag" value={s.staffingHeroTag || ""} onChange={v => setSetting("staffingHeroTag", v)} />
          <Fld label="CTA Button Text" value={s.staffingCtaBtn || ""} onChange={v => setSetting("staffingCtaBtn", v)} />
        </div>
        <Fld label="Hero Headline" value={s.staffingHeroTitle || ""} onChange={v => setSetting("staffingHeroTitle", v)} />
        <Fld label="Hero Subheading" value={s.staffingHeroSub || ""} onChange={v => setSetting("staffingHeroSub", v)} rows={2} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Fld label="Bottom CTA Title" value={s.staffingCtaTitle || ""} onChange={v => setSetting("staffingCtaTitle", v)} />
          <Fld label="Bottom CTA Subtext" value={s.staffingCtaSub || ""} onChange={v => setSetting("staffingCtaSub", v)} />
        </div>
      </Section>

      {/* ── SOFTWARE / IT PAGE ── */}
      <Section icon="💻" gradient="linear-gradient(135deg,#2557A7,#7caeed)" title="Software / IT Page" tag="hero · cta">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Fld label="Hero Badge Tag" value={s.itHeroTag || ""} onChange={v => setSetting("itHeroTag", v)} />
          <Fld label="CTA Button Text" value={s.itCtaBtn || ""} onChange={v => setSetting("itCtaBtn", v)} />
        </div>
        <Fld label="Hero Headline" value={s.itHeroTitle || ""} onChange={v => setSetting("itHeroTitle", v)} />
        <Fld label="Hero Subheading" value={s.itHeroSub || ""} onChange={v => setSetting("itHeroSub", v)} rows={2} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Fld label="Bottom CTA Title" value={s.itCtaTitle || ""} onChange={v => setSetting("itCtaTitle", v)} />
          <Fld label="Bottom CTA Subtext" value={s.itCtaSub || ""} onChange={v => setSetting("itCtaSub", v)} />
        </div>
      </Section>

      {/* ── LAB PAGE ── */}
      <Section icon="🔬" gradient="linear-gradient(135deg,#0F6E56,#22c55e)" title="Lab Page" tag="hero · promo banner">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Fld label="Hero Badge Tag" value={s.labHeroTag || ""} onChange={v => setSetting("labHeroTag", v)} />
          <Fld label="Hero Headline" value={s.labHeroTitle || ""} onChange={v => setSetting("labHeroTitle", v)} />
        </div>
        <Fld label="Hero Subheading" value={s.labHeroSub || ""} onChange={v => setSetting("labHeroSub", v)} rows={2} />
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 9, padding: "13px 15px", marginTop: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#15803d" }}>🔬 Promo Banner (green strip under nav)</div>
            <Toggle on={s.labBannerEnabled} onToggle={() => setSetting("labBannerEnabled", !s.labBannerEnabled)} />
            <span style={{ fontSize: 12, color: s.labBannerEnabled ? "#15803d" : "#94a3b8", fontWeight: 600 }}>{s.labBannerEnabled ? "ON" : "OFF"}</span>
          </div>
          <Fld label="Banner Message" value={s.labBannerText || ""} onChange={v => setSetting("labBannerText", v)} />
        </div>
      </Section>

      {/* ── VALUATION PAGE ── */}
      <Section icon="🏢" gradient="linear-gradient(135deg,#b45309,#fbbf24)" title="Valuation Page" tag="hero · cta">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Fld label="Hero Badge Tag" value={s.valuationHeroTag || ""} onChange={v => setSetting("valuationHeroTag", v)} />
          <Fld label="CTA Button Text" value={s.valuationCtaBtn || ""} onChange={v => setSetting("valuationCtaBtn", v)} />
        </div>
        <Fld label="Hero Headline" value={s.valuationHeroTitle || ""} onChange={v => setSetting("valuationHeroTitle", v)} />
        <Fld label="Hero Subheading" value={s.valuationHeroSub || ""} onChange={v => setSetting("valuationHeroSub", v)} rows={2} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Fld label="CTA Section Title" value={s.valuationCtaTitle || ""} onChange={v => setSetting("valuationCtaTitle", v)} />
          <Fld label="CTA Section Subtext" value={s.valuationCtaSub || ""} onChange={v => setSetting("valuationCtaSub", v)} />
        </div>
      </Section>

      {/* ── BLOG PAGE ── */}
      <Section icon="✍️" gradient="linear-gradient(135deg,#0F6E56,#5ecba8)" title="Blog Page" tag="hero · empty state">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Fld label="Page Title" value={s.blogHeroTitle || ""} onChange={v => setSetting("blogHeroTitle", v)} />
          <Fld label="Featured Post Tag Label" value={s.blogFeaturedTag || ""} onChange={v => setSetting("blogFeaturedTag", v)} />
        </div>
        <Fld label="Page Subheading" value={s.blogHeroSub || ""} onChange={v => setSetting("blogHeroSub", v)} rows={2} />
        <Fld label="Empty State Message (no posts)" value={s.blogEmptyMsg || ""} onChange={v => setSetting("blogEmptyMsg", v)} />
      </Section>

      {/* ── COMPANY INFO ── */}
      <Section icon="🏢" gradient="linear-gradient(135deg,#2557A7,#7caeed)" title="Company Information">
        <Fld label="Full Company Name" value={s.companyName || ""} onChange={v => setSetting("companyName", v)} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Fld label="Email" value={s.email || ""} onChange={v => setSetting("email", v)} />
          <Fld label="Phone" value={s.phone || ""} onChange={v => setSetting("phone", v)} />
        </div>
        <Fld label="Address" value={s.address || ""} onChange={v => setSetting("address", v)} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Fld label="GSTIN" value={s.gstin || ""} onChange={v => setSetting("gstin", v)} />
          <Fld label="WhatsApp Number" value={s.waNumber || ""} onChange={v => setSetting("waNumber", v)} />
        </div>
      </Section>

      {/* ── SEO ── */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 13, padding: "22px 22px 18px", boxShadow: "0 2px 8px rgba(0,0,0,.04)", marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ width: 32, height: 32, background: "linear-gradient(135deg,#0F6E56,#5ecba8)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>🔍</div>
          <h3 style={{ color: C.blue, fontWeight: 800, fontSize: 15, margin: 0 }}>SEO Settings</h3>
        </div>
        <Fld label="SEO Title (max 60 chars)" value={s.seoTitle || ""} onChange={v => setSetting("seoTitle", v)} />
        <Fld label="SEO Description (max 155 chars)" value={s.seoDesc || ""} onChange={v => setSetting("seoDesc", v)} rows={3} />
        <Fld label="Keywords (comma separated)" value={s.seoKeywords || ""} onChange={v => setSetting("seoKeywords", v)} rows={2} />
        {s.seoTitle && (
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 9, padding: "12px 14px", marginTop: 10 }}>
            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, marginBottom: 8, textTransform: "uppercase", letterSpacing: .5 }}>Google Preview</div>
            <div style={{ fontSize: 14, color: "#1a0dab", fontWeight: 600, marginBottom: 2 }}>{(s.seoTitle || "").slice(0, 60)}</div>
            <div style={{ fontSize: 12, color: "#006621", marginBottom: 3 }}>cyclojetinfotech.com</div>
            <div style={{ fontSize: 12, color: "#545454", lineHeight: 1.6 }}>{(s.seoDesc || "").slice(0, 155)}</div>
          </div>
        )}
      </div>

      <ABt variant="orange" onClick={() => toast("Settings saved successfully")}>Save All Settings</ABt>
    </div>
  );
}
